import baselineCheckoutRoutes from '@/pages/baseline/checkout/routes'
import baselineOnboardingRoutes, {
  trialOnboardingQuestionRoutes,
} from '@/pages/baseline/onboarding/routes'
import quizOnboardingRoutes from '@/pages/quiz/onboarding/routes'
import shortOnboardingRoutes from '@/pages/short/onboarding/routes'
import v1CheckoutRoutes from '@/pages/v1/checkout/routes'
import v1OnboardingRoutes from '@/pages/v1/onboarding/routes'
import v2CheckoutRoutes from '@/pages/v2/checkout/routes'
import v2OnboardingRoutes from '@/pages/v2/onboarding/routes'
import type { ABTestRouteConfig } from '@app-studio-web/ui/types/abtest'

interface RouteDefinition {
  path?: string
  children?: RouteDefinition[]
}

function extractChildPaths(routesDef: RouteDefinition[]): string[] {
  const parent = routesDef[0]
  if (!parent || !parent.children) return []
  return parent.children
    .map((child) => child.path)
    .filter((path): path is string => Boolean(path))
}

function extractFlatPaths(routes: { path: string }[]): string[] {
  return routes.map((route) => route.path)
}

// --- Onboarding Route Config ---
// Baseline is the 31-screen funnel promoted from v2 of
// li-restore-onboarding-length. li-funnel-redesign keeps that question set and
// order in v1 and only restyles it, while v2 replaces it with the Duolingo
// register flow, so each variant carries its own ordering.
const baselineOnboardingOrder = extractChildPaths(
  baselineOnboardingRoutes as RouteDefinition[],
)
const v1OnboardingOrder = extractChildPaths(
  v1OnboardingRoutes as RouteDefinition[],
)
const v2OnboardingOrder = extractChildPaths(
  v2OnboardingRoutes as RouteDefinition[],
)

export const lingoChatABTestRouteConfig: ABTestRouteConfig = {
  baseline: baselineOnboardingOrder,
  variants: {
    v1: v1OnboardingOrder,
    v2: v2OnboardingOrder,
  },
}

// --- Trial Onboarding Route Config ---
// The trial funnel has no landing page and keeps the language question at its
// 'question-language' entry, so it needs its own ordering independent of the
// regular funnel (whose first page is now the landing page).
export const trialABTestRouteConfig: ABTestRouteConfig = {
  baseline: extractFlatPaths(trialOnboardingQuestionRoutes),
  variants: {},
}

// --- Vo Onboarding Route Config ---
// The "vo" funnel now runs the same cut-down (short) question sequence as the
// /onboarding short funnel; only the entry URL, sticky flag and checkout plans
// page (the 1/3/12-month -vo pricing) differ.
export const voABTestRouteConfig: ABTestRouteConfig = {
  baseline: extractChildPaths(shortOnboardingRoutes as RouteDefinition[]),
  variants: {},
}

// --- Quiz Onboarding Route Config ---
// The standalone quiz funnel is a single fixed sequence with no A/B variants.
// It starts with the Flow landing page ('question-language') and runs its own
// short set of quiz questions, so it needs its own ordering.
export const quizABTestRouteConfig: ABTestRouteConfig = {
  baseline: extractChildPaths(quizOnboardingRoutes as RouteDefinition[]),
  variants: {},
}

// --- Short Onboarding Route Config ---
// The standalone short funnel is a fixed 20-page cut-down of the baseline
// funnel with no A/B variants. It reuses the baseline page components and runs
// its own shorter ordering starting with the Flow landing page.
export const shortABTestRouteConfig: ABTestRouteConfig = {
  baseline: extractChildPaths(shortOnboardingRoutes as RouteDefinition[]),
  variants: {},
}

// --- Checkout A/B Test Route Config ---
const baselineCheckoutOrder = extractChildPaths(
  baselineCheckoutRoutes as RouteDefinition[],
)

const v1CheckoutOrder = extractChildPaths(v1CheckoutRoutes as RouteDefinition[])
const v2CheckoutOrder = extractChildPaths(v2CheckoutRoutes as RouteDefinition[])

export const lingoChatCheckoutABTestRouteConfig: ABTestRouteConfig = {
  baseline: baselineCheckoutOrder,
  variants: {
    v1: v1CheckoutOrder,
    v2: v2CheckoutOrder,
  },
}

export function getABTestConfig() {
  const clientSecret = import.meta.env.VITE_CONFIDENCE_CLIENT_SECRET as string
  const flagName =
    (import.meta.env.VITE_CONFIDENCE_FLAG_NAME as string) ||
    'li-funnel-redesign'

  if (!clientSecret) {
    console.warn('No Confidence client secret provided, A/B testing disabled')
    return null
  }

  return {
    clientSecret,
    flagName,
    defaultVariant: 'baseline',
    enabledVariants: ['baseline', 'v1', 'v2'],
  }
}
